import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';
import type { Weekday } from '@/modules/products/passes';

/**
 * Multi-Visit Pass Product Definitions (ADR 0064): a venue-scoped, self-contained product — a
 * name, a waiver flag, an active flag and one or more MVP options. Each option is a whole number
 * of hours at a preset price (a decimal string in the Venue's currency, applied server-side),
 * valid for `validity.amount` DAYS or MONTHS from sale, optionally only on some weekdays (an empty
 * list = every day). The editor's one Save is a single PUT carrying the whole option set.
 */
export type MvpValidityUnit = 'DAYS' | 'MONTHS';

export const MVP_VALIDITY_UNITS: MvpValidityUnit[] = ['DAYS', 'MONTHS'];

export interface MvpValidity {
  amount: number;
  unit: MvpValidityUnit;
}

export interface MvpOption {
  id: string;
  name: string;
  hours: number;
  price: string;
  currency: string;
  validity: MvpValidity;
  validWeekdays: Weekday[];
  active: boolean;
  soldCount: number;
}

export interface MvpDefinition {
  id: string;
  venueId: string;
  name: string;
  waiverRequired: boolean;
  active: boolean;
  options: MvpOption[];
  createdAt: string;
  updatedAt: string;
}

export interface MvpDefinitionList {
  items: MvpDefinition[];
}

/** One option row on the wire. No `id` = a new option; an omitted `active` defaults to on sale. */
export interface MvpOptionInput {
  id?: string;
  name: string;
  hours: number;
  price: string;
  validity: MvpValidity;
  validWeekdays: Weekday[];
  active: boolean;
}

export interface CreateMvpDefinitionInput {
  name: string;
  waiverRequired: boolean;
  options: MvpOptionInput[];
}

export interface EditMvpDefinitionInput extends CreateMvpDefinitionInput {
  active: boolean;
}

const venueKey = (venueId: string) => ['mvp-definitions', venueId] as const;

const getMvpDefinitions = (venueId: string): Promise<MvpDefinitionList> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/multi-visit-pass-definitions', {
      params: { path: { venueId } },
    }),
  ) as Promise<MvpDefinitionList>;

const createMvpDefinition = (
  venueId: string,
  input: CreateMvpDefinitionInput,
): Promise<MvpDefinition> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/multi-visit-pass-definitions', {
      params: { path: { venueId } },
      body: input,
    }),
  ) as Promise<MvpDefinition>;

const editMvpDefinition = (
  venueId: string,
  definitionId: string,
  input: EditMvpDefinitionInput,
): Promise<MvpDefinition> =>
  unwrap(
    apiClient.PUT(
      '/api/v1/venue-workspace/venues/{venueId}/multi-visit-pass-definitions/{definitionId}',
      { params: { path: { venueId, definitionId } }, body: input },
    ),
  ) as Promise<MvpDefinition>;

const deleteMvpDefinition = async (venueId: string, definitionId: string): Promise<void> => {
  await unwrap(
    apiClient.DELETE(
      '/api/v1/venue-workspace/venues/{venueId}/multi-visit-pass-definitions/{definitionId}',
      {
        params: { path: { venueId, definitionId } },
      },
    ),
  );
};

const setMvpDefinitionActive = (
  venueId: string,
  definitionId: string,
  active: boolean,
): Promise<MvpDefinition> =>
  unwrap(
    apiClient.PATCH(
      '/api/v1/venue-workspace/venues/{venueId}/multi-visit-pass-definitions/{definitionId}/active',
      { params: { path: { venueId, definitionId } }, body: { active } },
    ),
  ) as Promise<MvpDefinition>;

export function useMvpDefinitions(venueId: string, enabled = true) {
  return useQuery({
    queryKey: venueKey(venueId),
    queryFn: () => getMvpDefinitions(venueId),
    enabled: enabled && !!venueId,
  });
}

export function useMvpDefinition(venueId: string, definitionId: string, enabled = true) {
  return useQuery({
    queryKey: [...venueKey(venueId), 'detail', definitionId],
    queryFn: async () => {
      const list = await getMvpDefinitions(venueId);
      return list.items.find((definition) => definition.id === definitionId) ?? null;
    },
    enabled: enabled && !!venueId && !!definitionId,
  });
}

export function useCreateMvpDefinition(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMvpDefinitionInput) => createMvpDefinition(venueId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: venueKey(venueId) }),
  });
}

export function useEditMvpDefinition(venueId: string, definitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EditMvpDefinitionInput) => editMvpDefinition(venueId, definitionId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: venueKey(venueId) }),
  });
}

export function useDeleteMvpDefinition(venueId: string, definitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteMvpDefinition(venueId, definitionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: venueKey(venueId) }),
  });
}

export function useSetMvpDefinitionActive(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ definitionId, active }: { definitionId: string; active: boolean }) =>
      setMvpDefinitionActive(venueId, definitionId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: venueKey(venueId) }),
  });
}
